import { siUnitsVisible } from '../../utils/siUnitsVisibility';
import { UnitSystem } from './unitConversions';

/**
 * Structural rather than importing Client/UserProfile: this module is reached
 * from PanelMarker and PanelField, which the models barrel evaluates early, and
 * importing the classes here would put this file inside that cycle. Nothing
 * needs more than these two fields anyway.
 */
export interface UnitSystemSources {
  /** The client record, or the client snapshot embedded on a Bloodwork. */
  client?: { units?: 'US' | 'SI' } | null;
  /** The signed-in practitioner's profile. */
  profile?: { unitSystem?: UnitSystem } | null;
}

/**
 * Which unit system to display and accept input in.
 *
 * Precedence — client wins, profile is the default:
 *
 *   1. client.units      the per-client radio on Add/Edit Client. A Canadian
 *                        practice with one American client still reads that
 *                        client's report in mg/dL.
 *   2. profile.unitSystem the practitioner's default, set once in Report Settings.
 *   3. 'US'              what every existing account has always seen.
 *
 * On a saved test the caller passes `bloodwork.client` — the snapshot stamped
 * at save time — so a report keeps reading in the units it was entered in even
 * if the client record is later edited. Same snapshot the height/weight display
 * already relies on.
 *
 * Returns 'US' unconditionally while the feature is gated off, which is what
 * lets every call site stay ungated.
 */
export function resolveUnitSystem(sources?: UnitSystemSources): UnitSystem {
  if (!siUnitsVisible()) return 'US';
  if (!sources) return 'US';
  const { client, profile } = sources;
  if (client?.units === 'SI' || client?.units === 'US') return client.units;
  if (profile?.unitSystem === 'SI' || profile?.unitSystem === 'US') {
    return profile.unitSystem;
  }
  return 'US';
}
